import * as React from 'react'
import { Toast, useToast } from '@renderer/overlay/Toaster'
import { type Summary, type SummaryItem } from '@renderer/chat/types'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { stripDailySummaryTitle } from '@shared/utils/summary-utils'
import { cleanArtifactTextForCopy } from '@renderer/windows/components/summary/utils/clean-artifact-text'

interface DailySummaryToastProps {
  toastId: string
  summary: Summary | SummaryItem
  timePeriod: 'Yesterday' | 'Last Week'
}

export const DailySummaryToast = ({ toastId, summary, timePeriod }: DailySummaryToastProps) => {
  const toast = useToast()

  const handleOpenSummary = React.useCallback(() => {
    toast.dismiss(toastId)
    // Navigate to summaries page with summaryId param to open the modal
    window.open(`highlight://memory/summaries?summaryId=${summary.id}`, '_blank')
    AmplitudeRenderer.trackEvent('Summary Toast', { action: 'Open', timePeriod })
  }, [toast, toastId, summary.id, timePeriod])

  const description = React.useMemo(() => {
    // If we have the full summary text, show a preview
    if ('summary' in summary && summary.summary) {
      const cleanedSummary = cleanArtifactTextForCopy(stripDailySummaryTitle(summary.summary))
      const maxLength = 120
      if (cleanedSummary.length <= maxLength) {
        return cleanedSummary
      }
      return cleanedSummary.slice(0, maxLength).trim() + '...'
    }

    // Fallback to generic descriptions if summary text is not available
    return timePeriod === 'Yesterday'
      ? "Here's a quick recap of your day yesterday."
      : "Here's a quick recap of how you ended last week."
  }, [summary, timePeriod])

  return (
    <Toast toastId={toastId}>
      <Toast.Content>
        <Toast.Description>{description}</Toast.Description>
        <Toast.Actions
          actions={[
            {
              onClick: handleOpenSummary,
              children: 'Read Full Summary',
            },
          ]}
        />
      </Toast.Content>
    </Toast>
  )
}
